import { zCustom } from '@nucleus/lib-shape';
import { ISO8601Schema } from '@nucleus/src-platform/data';
import * as yup from 'yup';
import { z } from 'zod';

/**
 * Metadata computed from entity, version, and schedules that provides a unified view of the publishable's state.
 *
 * This type is derived from the Zod schema and includes properties from three sources:
 * - Entity properties (createdAt, createdBy)
 * - Version properties (latestRevision* fields)
 * - Schedule-derived properties (publishedAt, publishedBy, unpublishedAt, unpublishedBy)
 */
export interface PublishableMetadata {
  /**
   * When the entity was first created.
   * Source: `entity.createdAt`
   */
  createdAt: string;

  /**
   * User ID who created the entity.
   * Source: `entity.createdBy`
   */
  createdBy: string;

  /**
   * Timestamp of when the most recent revision was created.
   * Source: `version.latestRevisionAt`
   * Updated whenever a new revision is created.
   */
  latestRevisionAt: string;

  /**
   * User ID who created the most recent revision.
   * Source: `version.latestRevisionBy`
   */
  latestRevisionBy: string;

  /**
   * The index number of the most recent revision.
   * Source: `version.latestRevisionIndex`
   * Increments with each new revision.
   */
  latestRevisionIndex: number;

  /**
   * When the currently published revision was published.
   * Source: `version.latestRevisionPublishedAt`
   * Only set if there is a published revision.
   * Updated when a revision is published or unpublished.
   */
  latestRevisionPublishedAt?: string;

  /**
   * User ID who published the currently published revision.
   * Source: `version.latestRevisionPublishedBy`
   * Only set if there is a published revision.
   */
  latestRevisionPublishedBy?: string;

  /**
   * The index of the currently published revision.
   * Source: `version.latestRevisionPublishedIndex`
   * Only set if there is a published revision.
   * When unpublished, this may point to a previous revision or be cleared.
   */
  latestRevisionPublishedIndex?: number;

  /**
   * When the publishable is/was published (or will be published).
   * Computed from schedules by finding the most recent publish action before `asOf`.
   * If currently unpublished but has a future publish scheduled, this will be the future publish date.
   * If currently published, this is the date of the most recent publish action.
   * `undefined` if never published and no future publish scheduled.
   */
  publishedAt?: string;

  /**
   * User ID who published (or will publish) the content.
   * Source: `schedule.createdBy` for the schedule that set `publishedAt`.
   * `undefined` if `publishedAt` is `undefined`.
   */
  publishedBy?: string;

  /**
   * When the publishable is/was unpublished (or will be unpublished).
   * Computed from schedules by finding unpublish actions.
   * If currently published but has a future unpublish scheduled, this will be the future unpublish date.
   * If currently unpublished, this is the date of the unpublish action.
   * `undefined` if never unpublished and no future unpublish scheduled.
   */
  unpublishedAt?: string;

  /**
   * User ID who unpublished (or will unpublish) the content.
   * Source: `schedule.createdBy` for the schedule that set `unpublishedAt`.
   * `undefined` if `unpublishedAt` is `undefined`.
   */
  unpublishedBy?: string;
}

export const PublishableMetadataSchema = z.object({
  createdAt: zCustom.iso(),
  createdBy: z.string().trim(),
  latestRevisionAt: zCustom.iso(),
  latestRevisionBy: z.string().trim(),
  latestRevisionIndex: z.number().min(1),
  latestRevisionPublishedAt: zCustom.iso().optional(),
  latestRevisionPublishedBy: z.string().trim().optional(),
  latestRevisionPublishedIndex: z.number().min(1).optional(),
  publishedAt: zCustom.iso().optional(),
  publishedBy: z.string().trim().optional(),
  unpublishedAt: zCustom.iso().optional(),
  unpublishedBy: z.string().trim().optional(),
}) satisfies z.ZodType<PublishableMetadata>;

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
export const PublishableMetadataSchemaDeprecated: yup.SchemaOf<PublishableMetadata> = yup.object({
  createdAt: ISO8601Schema.required(),
  createdBy: yup.string().required(),
  latestRevisionAt: ISO8601Schema.required(),
  latestRevisionBy: yup.string().required(),
  latestRevisionIndex: yup.number().min(1).required(),
  latestRevisionPublishedAt: ISO8601Schema,
  latestRevisionPublishedBy: yup.string(),
  latestRevisionPublishedIndex: yup.number().min(1),
  publishedAt: ISO8601Schema,
  publishedBy: yup.string(),
  unpublishedAt: ISO8601Schema,
  unpublishedBy: yup.string(),
});
